import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import repositoryContainer from '../../services/RepositoryContainer';
import { useCompany } from '../context/CompanyContext';
import ItemThumbnail from '../ui/ItemThumbnail';
import PaymentSummary from '../payments/PaymentSummary';
import ConversationButton from '../chat/ConversationButton';
import ConversationModal from '../chat/ConversationModal';
import { getChatAccessToken, hasConversationNote } from '../../utils/chatAccess';
import { formatItemPrice, needsPriceFinalization } from '../../utils/pricingUtils';

const CONFIG = {
  order: {
    steps: [
      { key: 'pending',       label: 'Order Placed' },
      { key: 'processing',    label: 'Processing' },
      { key: 'readyforpickup',label: 'Ready' },
      { key: 'completed',     label: 'Completed' },
    ],
    status: {
      pending:       { label: 'Pending',    color: 'text-warning',     bg: 'bg-warning/10',     border: 'border-warning/20',     icon: 'Clock' },
      processing:    { label: 'Processing', color: 'text-primary',     bg: 'bg-primary/10',     border: 'border-primary/20',     icon: 'Loader2' },
      readyforpickup:{ label: 'Ready',      color: 'text-secondary',   bg: 'bg-secondary/10',   border: 'border-secondary/20',   icon: 'PackageCheck' },
      completed:     { label: 'Completed',  color: 'text-success',     bg: 'bg-success/10',     border: 'border-success/20',     icon: 'CheckCircle2' },
      cancelled:     { label: 'Cancelled',  color: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/20', icon: 'XCircle' },
    },
  },
  booking: {
    steps: [
      { key: 'pending',    label: 'Booked' },
      { key: 'confirmed',  label: 'Confirmed' },
      { key: 'inprogress', label: 'In Service' },
      { key: 'completed',  label: 'Done' },
    ],
    status: {
      pending:    { label: 'Pending',     color: 'text-warning',     bg: 'bg-warning/10',     border: 'border-warning/20',     icon: 'CalendarClock' },
      confirmed:  { label: 'Confirmed',   color: 'text-success',     bg: 'bg-success/10',     border: 'border-success/20',     icon: 'CalendarCheck' },
      inprogress: { label: 'In Progress', color: 'text-primary',     bg: 'bg-primary/10',     border: 'border-primary/20',     icon: 'Zap' },
      completed:  { label: 'Completed',   color: 'text-secondary',   bg: 'bg-secondary/10',   border: 'border-secondary/20',   icon: 'CheckCircle2' },
      cancelled:  { label: 'Cancelled',   color: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/20', icon: 'CalendarX' },
    },
  },
};

function deriveProgress(data, type) {
  if (!data) return { lastReachedIndex: -1, isTerminal: false, currentStatus: 'pending' };
  const status = String(data.status || 'pending').toLowerCase().replace(/[\s_-]/g, '');
  const steps = CONFIG[type].steps;
  const isTerminal = ['cancelled', 'noshow'].includes(status);
  // Payment history and payment fields must never advance operational progress.
  // A terminal status proves placement, but not any later processing stage.
  const lastReachedIndex = isTerminal ? 0 : steps.findIndex(step => step.key === status);
  return { lastReachedIndex, isTerminal, currentStatus: status };
}

const StatusBadge = ({ type, status }) => {
  const cfg = CONFIG[type].status[status?.toLowerCase()] || CONFIG[type].status.pending;
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.color} ${cfg.border}`}>
      <Icon name={cfg.icon} size={12} strokeWidth={2.5} />
      {cfg.label}
    </div>
  );
};

const VerticalTimeline = ({ type, lastReachedIndex, isTerminal, currentStatus }) => {
  const steps = CONFIG[type].steps;
  const isCompleted = currentStatus === 'completed';

  return (
    <div className="relative space-y-1">
      {steps.map((step, i) => {
        const isPast    = i <= lastReachedIndex;
        const isCurrent = !isTerminal && !isCompleted && i === lastReachedIndex && i > 0;
        const isDone    = isCompleted || i < lastReachedIndex || (i === 0 && lastReachedIndex === 0);

        return (
          <div key={step.key} className="flex gap-3 group">
            <div className="flex flex-col items-center">
              <div className={`z-10 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-500 ${
                isDone    ? 'bg-success scale-110' :
                isCurrent ? 'bg-card border-2 border-primary ring-4 ring-primary/10' :
                            'bg-muted border border-border'
              }`}>
                {isDone && <Icon name="Check" size={14} color="white" strokeWidth={3} />}
              </div>
              {i !== steps.length - 1 && (
                <div className={`w-0.5 h-10 -my-1 transition-colors duration-500 ${i < lastReachedIndex ? 'bg-success' : 'bg-border'}`} />
              )}
            </div>
            <div className="pt-1 pb-5">
              <p className={`text-sm font-semibold transition-colors ${isPast || isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </p>
              {isCurrent && (
                <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Active stage</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TrackingModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { companyPath } = useCompany();
  const [tab, setTab]         = useState('order');
  const [code, setCode]       = useState('');
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [paymentEmail, setPaymentEmail] = useState('');
  const [isStartingPayment, setIsStartingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isConversationOpen, setIsConversationOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCode(''); setResult(null); setError(''); setPaymentEmail(''); setPaymentError(''); setIsConversationOpen(false);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  const handleTrack = async () => {
    if (!code.trim()) return;

    if (tab === 'request') {
      onClose();
      navigate(`/track/${encodeURIComponent(code.trim())}`);
      return;
    }

    setIsLoading(true); setError('');
    try {
      const data = tab === 'order'
        ? await repositoryContainer.order.getByReferenceCode(code.trim())
        : await repositoryContainer.booking.getByReferenceCode(code.trim());
      const normalized = data?.response ?? data;
      setResult(normalized);
      setPaymentEmail(normalized?.clientEmail || '');
    } catch {
      setError(`No ${tab} found for that reference code.`);
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const normalizedResultStatus = result?.status?.toString().toLowerCase().replace(/[\s_-]/g, '');
  const resultNeedsFinalPricing = tab === 'booking' && needsPriceFinalization(result);
  const canPay = Number(result?.balanceDue) > 0
    && !['cancelled', 'rejected', 'noshow'].includes(normalizedResultStatus)
    && Boolean(companyPath)
    && Boolean(result?.id)
    && !resultNeedsFinalPricing;
  const bookingChatToken = tab === 'booking' && result?.referenceCode
    ? getChatAccessToken('booking', result.referenceCode)
    : '';
  const bookingNote = result?.companyNote || result?.notes || result?.note;

  const handlePayment = async () => {
    if (!result?.id || !companyPath || isStartingPayment || resultNeedsFinalPricing) return;
    if (tab === 'order' && !/^\S+@\S+\.\S+$/.test(paymentEmail.trim())) {
      setPaymentError('Enter the email address to use for payment.');
      return;
    }

    setIsStartingPayment(true);
    setPaymentError('');
    try {
      const callbackUrl = `${window.location.origin}/${companyPath}/${tab === 'order' ? 'order-callback' : 'booking-callback'}`;
      const raw = tab === 'order'
        ? await repositoryContainer.order.initiatePayment(result.id, paymentEmail.trim(), callbackUrl)
        : await repositoryContainer.booking.initiatePayment(result.id, callbackUrl);
      const payment = raw?.response ?? raw;
      if (!payment?.authorizationUrl || !payment?.reference) throw new Error('Payment checkout is unavailable.');

      if (tab === 'order') {
        localStorage.setItem(`clawfe:pendingOrder:${companyPath}`, JSON.stringify({
          orderId: result.id,
          referenceCode: payment.sourceReferenceCode || result.referenceCode,
          email: paymentEmail.trim(),
          paymentReference: payment.reference,
        }));
      } else {
        localStorage.setItem(`clawfe:pendingBooking:${companyPath}`, JSON.stringify({
          referenceCode: payment.sourceReferenceCode || result.referenceCode,
          confirmationNumber: payment.sourceReferenceCode || result.referenceCode,
          paymentReference: payment.reference,
          isBalancePayment: Number(result.amountPaid) > 0,
        }));
      }
      window.location.assign(payment.authorizationUrl);
    } catch (paymentStartError) {
      setPaymentError(paymentStartError?.data?.message || paymentStartError?.message || 'Payment could not be started.');
      setIsStartingPayment(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/60 backdrop-blur-sm">
      <div
        className="relative bg-card text-foreground w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh] border border-border"
        style={{ animation: 'trackSlideUp 0.4s cubic-bezier(0.16,1,0.3,1)' }}
      >

        {/* ── Search Sidebar ── */}
        <div className="w-full md:w-[300px] bg-muted/60 p-6 flex flex-col border-r border-border flex-shrink-0">

          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl text-foreground">Track</h2>
            <button
              onClick={onClose}
              className="md:hidden p-1.5 rounded-lg hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Tab switcher */}
          <div className="relative flex p-1 bg-background rounded-xl mb-6 border border-border">
            <div
              className="absolute top-1 bottom-1 transition-all duration-300 bg-card rounded-lg shadow-sm border border-border"
              style={{
                width: 'calc(33.333% - 2.667px)',
                left: tab === 'order' ? '4px' : tab === 'booking' ? '33.333%' : '66.666%',
              }}
            />
            <button
              onClick={() => { setTab('order'); setResult(null); }}
              className={`relative z-10 flex-1 py-2 text-xs font-bold transition-colors ${tab === 'order' ? 'text-primary' : 'text-muted-foreground'}`}
            >
              Orders
            </button>
            <button
              onClick={() => { setTab('booking'); setResult(null); setError(''); }}
              className={`relative z-10 flex-1 py-2 text-xs font-bold transition-colors ${tab === 'booking' ? 'text-primary' : 'text-muted-foreground'}`}
            >
              Bookings
            </button>
            <button
              onClick={() => { setTab('request'); setResult(null); setError(''); }}
              className={`relative z-10 flex-1 py-2 text-xs font-bold transition-colors ${tab === 'request' ? 'text-primary' : 'text-muted-foreground'}`}
            >
              Requests
            </button>
          </div>

          {/* Input + CTA */}
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
                Reference Code
              </label>
              <input
                ref={inputRef}
                value={code}
                onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleTrack()}
                placeholder={tab === 'order' ? 'ORD-...' : tab === 'booking' ? 'BK-...' : 'REQ...'}
                className="w-full bg-card border border-border rounded-xl px-4 py-3 text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>

            <button
              onClick={handleTrack}
              disabled={isLoading || !code.trim()}
              className="w-full bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm"
            >
              {isLoading
                ? <Icon name="Loader2" className="animate-spin" size={16} />
                : <Icon name="Search" size={16} />}
              Track Now
            </button>

            {error && (
              <p className="text-destructive text-xs font-medium text-center">{error}</p>
            )}
          </div>
        </div>

        {/* ── Desktop close button ── */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-lg hover:bg-muted hidden md:flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="X" size={18} />
        </button>

        {/* ── Results Pane ── */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">

          {!result ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon
                  name={tab === 'order' ? 'ShoppingBag' : tab === 'booking' ? 'Calendar' : 'ClipboardList'}
                  size={28}
                  className="text-muted-foreground"
                />
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-1">Ready to track?</h3>
              <p className="text-sm text-muted-foreground max-w-[220px]">Enter your reference code to view real-time status.</p>
            </div>

          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">

              {/* Result header */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6 pb-6 border-b border-border">
                <div>
                  <h3 className="font-heading font-bold text-xl text-foreground">
                    {tab === 'order' ? 'Order Summary' : (result?.serviceName || 'Booking Details')}
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground mt-1">{result?.referenceCode}</p>
                </div>
                <StatusBadge type={tab} status={result?.status} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Timeline */}
                <div className="lg:col-span-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-5">Progress</p>
                  <VerticalTimeline type={tab} {...deriveProgress(result, tab)} />
                </div>

                {/* Details */}
                <div className="lg:col-span-7 space-y-6">

                  <div className="bg-muted/50 rounded-xl p-5 border border-border">
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Details</p>

                    {tab === 'order' ? (
                      <div className="space-y-3">
                        {(result?.items || []).map((item, i) => (
                          <div key={item.serviceItemId || i} className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <ItemThumbnail src={item.imageUrl} alt={item.itemName || item.name} size="sm" fallbackIcon="Package" />
                              <span className="min-w-0 text-sm text-foreground">
                                <span className="text-muted-foreground mr-1">×{item?.quantity}</span>
                                {item?.itemName || item?.name}
                              </span>
                            </div>
                            <span className="text-sm font-semibold text-foreground">
                              GH¢{Number(item?.totalPrice ?? ((Number(item?.unitPrice) || 0) * (Number(item?.quantity) || 0))).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        <div className="pt-4 mt-1 border-t border-border flex justify-between items-center">
                          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total</span>
                          <span className="font-heading font-bold text-xl text-primary">
                            GH¢{result?.totalAmount?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                      </div>

                    ) : (
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground mb-1">Date</p>
                          <p className="text-sm font-semibold text-foreground">
                            {result?.date ? new Date(result.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{result?.time || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground mb-1">Client</p>
                          <p className="text-sm font-semibold text-foreground">{result?.clientName || 'Guest'}</p>
                        </div>
                        {(result?.items || []).length > 0 && (
                          <div className="col-span-2 space-y-3 border-t border-border pt-4">
                            {(result.items || []).map((item, index) => (
                              <div key={item.serviceItemId || index} className="flex items-center gap-3">
                                <ItemThumbnail src={item.imageUrl} alt={item.name || item.itemName} size="sm" fallbackIcon="Scissors" />
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium text-foreground">{item.name || item.itemName || result.serviceName}</p>
                                  {item.unitPrice != null && <p className="text-xs text-muted-foreground">{formatItemPrice(item)}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {tab === 'booking' && bookingNote && (
                    <div className="rounded-xl bg-muted/50 p-5">
                      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">Notes</p>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{bookingNote}</p>
                    </div>
                  )}

                  {tab === 'booking' && bookingChatToken && (
                    <ConversationButton
                      hasNote={hasConversationNote(bookingNote)}
                      onClick={() => setIsConversationOpen(true)}
                    />
                  )}

                  {(result.totalAmount != null || result.amountPaid != null || result.balanceDue != null) && (
                    <div className="rounded-xl bg-muted/50 p-5">
                      <p className="mb-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">Payment</p>
                      {resultNeedsFinalPricing ? (
                        <div className="rounded-lg border border-warning/20 bg-warning/10 p-3">
                          <p className="text-sm font-medium text-foreground">Awaiting final price</p>
                          <p className="mt-1 text-xs text-muted-foreground">The business must confirm the service price before payment can begin.</p>
                        </div>
                      ) : (
                        <PaymentSummary record={result} totalAmount={result.totalAmount ?? result.price} compact />
                      )}
                      {canPay && (
                        <div className="mt-4 space-y-3">
                          {tab === 'order' && !result.clientEmail && (
                            <div>
                              <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Payment email</label>
                              <input
                                type="email"
                                value={paymentEmail}
                                onChange={(event) => { setPaymentEmail(event.target.value); setPaymentError(''); }}
                                placeholder="you@example.com"
                                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={handlePayment}
                            disabled={isStartingPayment}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                          >
                            <Icon name={isStartingPayment ? 'Loader2' : 'CreditCard'} size={16} className={isStartingPayment ? 'animate-spin' : ''} />
                            {Number(result.amountPaid) > 0 ? 'Pay Remaining Balance' : 'Pay Now'}
                          </button>
                        </div>
                      )}
                      {paymentError && <p className="mt-3 text-xs text-destructive">{paymentError}</p>}
                    </div>
                  )}

                  {/* Recent activity */}
                  {(result?.history || []).length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Recent Activity</p>
                      <div className="space-y-4">
                        {(result.history).slice().reverse().slice(0, 3).map((log, i) => (
                          <div key={i} className="flex gap-3 items-start">
                            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${i === 0 ? 'bg-primary' : 'bg-border'}`} />
                            <div>
                              <p className="text-sm font-medium text-foreground">{log?.action}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {log?.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConversationModal
        isOpen={isConversationOpen}
        onClose={() => setIsConversationOpen(false)}
        audience="customer"
        subjectType="booking"
        subjectId={result?.id}
        referenceCode={result?.referenceCode}
        chatAccessToken={bookingChatToken}
      />

      <style>{`
        @keyframes trackSlideUp {
          from { opacity: 0; transform: translateY(40px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default TrackingModal;
